import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, InputBase, Typography } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { endPoint } from "../../global";
import { useAuth } from "../../context/AuthProvider";
import Loading from "../../components/Loading";
import ResponseAlert from "../../components/ResponseAlert";

const poppins = { fontFamily: "'Poppins', sans-serif" };

export default function Offer() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [offer, setOffer] = useState([]);
  const [data, setData] = useState([]);
  const [draftQty, setDraftQty] = useState({});

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState("");
  const [success, setSuccess] = useState(true);
  const [openAlert, setOpenAlert] = useState(false);

  const formatRupiah = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(Number(value || 0));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const res = await axios.get(endPoint + "offer", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data.msg) {
          setOpenAlert(true);
          setResponse(res.data.msg);
          setSuccess(true);
        }

        setOffer(res.data.result || []);
        setData(res.data.initialData || []);
      } catch (error) {
        setOpenAlert(true);
        setResponse(error.response?.data?.msg ?? "Terjadi kesalahan.");
        setSuccess(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line
  }, []);

  const getQty = (packageId) => {
    return (
      Number(
        data.find((item) => item.packageId === String(packageId))?.quantity,
      ) || 0
    );
  };

  const getDisplayQty = (packageId) => {
    const key = String(packageId);

    if (draftQty[key] !== undefined) {
      return draftQty[key];
    }

    const qty = getQty(packageId);
    return qty === 0 ? "" : qty;
  };

  const getPackageByQuantity = (quantity) => {
    const qty = Number(quantity) || 0;

    if (qty <= 0) return null;

    return offer.find((item) => {
      const min = Number(item.minPurch) || 0;
      const max = Number(item.maxPurch) || 0;

      if (max === 0) {
        return qty >= min;
      }

      return qty >= min && qty <= max;
    });
  };

  const resetAllQuantity = () => {
    setData((prev) =>
      prev.map((item) => ({
        ...item,
        quantity: 0,
      })),
    );
  };

  const handleQuantityChange = (packageId, value) => {
    setDraftQty({});

    setData((prev) =>
      prev.map((item) => ({
        ...item,
        quantity: item.packageId === String(packageId) ? Number(value) || 0 : 0,
      })),
    );
  };

  const applyQuantityByRange = (value) => {
    const qty = Number(value) || 0;

    setDraftQty({});

    if (qty <= 0) {
      resetAllQuantity();
      return;
    }

    const selectedPackage = getPackageByQuantity(qty);

    if (!selectedPackage) {
      setOpenAlert(true);
      setResponse("Jumlah paket tidak sesuai dengan range paket.");
      setSuccess(false);
      return;
    }

    setData((prev) =>
      prev.map((item) => ({
        ...item,
        quantity:
          item.packageId === String(selectedPackage.packageId) ? qty : 0,
      })),
    );
  };

  const commitDraftQuantity = (packageId) => {
    const key = String(packageId);
    const raw = draftQty[key];

    if (raw === undefined) return;

    if (raw === "") {
      handleQuantityChange(packageId, 0);
      return;
    }

    applyQuantityByRange(raw);
  };

  const getFinalDataBeforeSubmit = () => {
    const activeDraftKey = Object.keys(draftQty).find(
      (key) => draftQty[key] !== undefined,
    );

    if (!activeDraftKey) return data;

    const raw = draftQty[activeDraftKey];

    if (!raw) {
      return data.map((item) => ({
        ...item,
        quantity: 0,
      }));
    }

    const selectedPackage = getPackageByQuantity(raw);

    if (!selectedPackage) return data;

    return data.map((item) => ({
      ...item,
      quantity:
        item.packageId === String(selectedPackage.packageId)
          ? Number(raw) || 0
          : 0,
    }));
  };

  const handlePesan = async () => {
    const finalData = getFinalDataBeforeSubmit();

    const totalSelected = finalData.reduce(
      (acc, item) => acc + (Number(item.quantity) || 0),
      0,
    );

    if (totalSelected === 0) {
      setOpenAlert(true);
      setResponse("Pilih minimal 1 paket terlebih dahulu.");
      setSuccess(false);
      return;
    }

    const selectedItem = finalData.find((item) => Number(item.quantity) > 0);

    if (!selectedItem) {
      setOpenAlert(true);
      setResponse("Jumlah paket tidak sesuai dengan range paket.");
      setSuccess(false);
      return;
    }

    setData(finalData);
    setDraftQty({});
    setLoading(true);

    try {
      const res = await axios.post(endPoint + "offer", finalData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setOpenAlert(true);
      setResponse(res.data.msg);
      setSuccess(true);
      setTimeout(() => navigate("/confirmation"), 1500);
    } catch (error) {
      setLoading(false);
      setOpenAlert(true);
      setResponse(error.response?.data?.msg ?? "Terjadi kesalahan.");
      setSuccess(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 480,
        mx: "auto",
        px: 2,
        py: 3,
        zIndex: 1,
        position: "relative",
      }}
    >
      <img
        src="SolarTuffNatGath.png"
        alt="Strength of Loyalty & Relationships"
        style={{ width: 80, objectFit: "contain" }}
      />

      <Box
        sx={{
          background:
            "linear-gradient(135deg, rgba(14,30,70,0.96), rgba(30,58,110,0.92))",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(147,197,253,0.22)",
          borderRadius: "14px",
          px: 2.5,
          py: 2,
          mb: 1.5,
          boxShadow: "0 8px 24px rgba(15,31,69,0.35)",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            ...poppins,
            fontWeight: 800,
            fontSize: "0.95rem",
            color: "#fff",
            letterSpacing: "0.3px",
            mb: 1,
            textTransform: "uppercase",
          }}
        >
          Paket Harga Spektakuler
        </Typography>

        <Box
          sx={{
            width: 56,
            height: 3,
            borderRadius: "999px",
            background: "rgba(255,255,255,0.75)",
            mx: "auto",
            mb: 1.2,
          }}
        />

        <Typography
          sx={{
            ...poppins,
            fontWeight: 700,
            fontSize: "0.92rem",
            color: "rgba(255,255,255,0.95)",
            lineHeight: 1.55,
            mb: 0.3,
          }}
        >
          1 (Satu) Paket Senilai
        </Typography>

        <Typography
          sx={{
            ...poppins,
            fontWeight: 800,
            fontSize: "1.15rem",
            color: "#fff",
            lineHeight: 1.4,
            mb: 0.4,
          }}
        >
          Rp 10.000.000
        </Typography>

        <Typography
          sx={{
            ...poppins,
            fontWeight: 500,
            fontSize: "0.8rem",
            color: "rgba(255,255,255,0.78)",
            lineHeight: 1.5,
          }}
        >
          Sepuluh Juta Rupiah • Incl PPN
        </Typography>
      </Box>

      <Box
        sx={{
          background: "rgba(255,255,255,0.92)",
          borderRadius: "14px",
          px: 2.5,
          py: 1.8,
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            ...poppins,
            fontWeight: 700,
            fontSize: "1.1rem",
            color: "#111",
          }}
        >
          Pilihan Jumlah Paket
        </Typography>
      </Box>

      {offer.map(
        (
          {
            packageId,
            namePackage,
            products,
            minPurch,
            maxPurch,
            extraDisc,
            uangElek,
          },
          index,
        ) => {
          const displayQty = getDisplayQty(packageId);
          const isDark = index % 2 === 1;

          return (
            <Box
              key={packageId}
              sx={{
                background: isDark
                  ? "rgba(14,30,70,0.88)"
                  : "rgba(255,255,255,0.92)",
                backdropFilter: "blur(10px)",
                border: isDark
                  ? "1px solid rgba(96,165,250,0.2)"
                  : "1px solid rgba(255,255,255,0.5)",
                borderRadius: "14px",
                px: 2.5,
                py: 2,
                mb: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      ...poppins,
                      fontWeight: 700,
                      fontSize: "1rem",
                      color: isDark ? "#fff" : "#0f1f45",
                      mb: 0.5,
                    }}
                  >
                    {namePackage}
                  </Typography>

                  <Typography
                    sx={{
                      ...poppins,
                      fontWeight: 500,
                      fontSize: "0.8rem",
                      color: isDark ? "rgba(255,255,255,0.8)" : "#475569",
                      lineHeight: 1.45,
                    }}
                  >
                    {Number(maxPurch) === 0
                      ? `Minimal pembelian ${minPurch} Paket`
                      : `${minPurch} - ${maxPurch} Paket`}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    flexShrink: 0,
                    mt: "1.85rem",
                    display: "grid",
                    rowGap: 0.35,
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "112px auto",
                      alignItems: "center",
                      columnGap: 0.6,
                    }}
                  >
                    <Typography
                      sx={{
                        ...poppins,
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        color: isDark ? "rgba(255,255,255,0.78)" : "#64748b",
                        lineHeight: 1.35,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Extra Disc
                    </Typography>

                    <Typography
                      sx={{
                        ...poppins,
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        color: isDark ? "#fff" : "#1e3a6e",
                        lineHeight: 1.35,
                        whiteSpace: "nowrap",
                        textAlign: "right",
                      }}
                    >
                      {Number(extraDisc || 0)}%
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "112px auto",
                      alignItems: "start",
                      columnGap: 0.6,
                    }}
                  >
                    <Typography
                      sx={{
                        ...poppins,
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        color: isDark ? "rgba(255,255,255,0.78)" : "#64748b",
                        lineHeight: 1.35,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Uang Elektronik
                    </Typography>

                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        sx={{
                          ...poppins,
                          fontSize: "0.72rem",
                          fontWeight: 800,
                          color: isDark ? "#fff" : "#1e3a6e",
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatRupiah(uangElek)}
                      </Typography>

                      <Typography
                        component="span"
                        sx={{
                          ...poppins,
                          display: "block",
                          mt: 0.15,
                          fontSize: "0.58rem",
                          fontWeight: 500,
                          color: isDark
                            ? "rgba(255,255,255,0.48)"
                            : "rgba(30,58,110,0.52)",
                          lineHeight: 1.15,
                          whiteSpace: "nowrap",
                        }}
                      >
                        (per Paket)
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  background: isDark ? "rgba(255,255,255,0.92)" : "#1e3a6e",
                  borderRadius: "50px",
                  px: 0.5,
                  py: 0.5,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    const currentValue = Number(displayQty) || 0;
                    const newQty = currentValue - 1;

                    if (newQty <= 0) {
                      handleQuantityChange(packageId, 0);
                      return;
                    }

                    applyQuantityByRange(newQty);
                  }}
                  sx={{
                    color: isDark ? "#1e3a6e" : "#fff",
                    p: 0.5,
                    "&:hover": { background: "rgba(0,0,0,0.08)" },
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>

                <InputBase
                  value={displayQty}
                  placeholder="0"
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    style: { textAlign: "center" },
                  }}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\D/g, "");

                    setDraftQty({
                      [String(packageId)]: raw,
                    });
                  }}
                  onBlur={() => commitDraftQuantity(packageId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.currentTarget.blur();
                    }
                  }}
                  sx={{
                    width: 80,
                    ...poppins,
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    color: isDark ? "#1e3a6e" : "#fff",
                    "& input": {
                      textAlign: "center",
                      color: isDark ? "#1e3a6e" : "#fff",
                      padding: 0,
                    },
                  }}
                />

                <Typography
                  sx={{
                    ...poppins,
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: isDark ? "#1e3a6e" : "#fff",
                    mr: 0.5,
                  }}
                >
                  Paket
                </Typography>

                <IconButton
                  size="small"
                  onClick={() => {
                    const currentValue = Number(displayQty) || 0;
                    const newQty = currentValue + 1;

                    applyQuantityByRange(newQty);
                  }}
                  sx={{
                    color: isDark ? "#1e3a6e" : "#fff",
                    p: 0.5,
                    "&:hover": { background: "rgba(0,0,0,0.08)" },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          );
        },
      )}

      <Button
        variant="contained"
        fullWidth
        onClick={handlePesan}
        sx={{
          ...poppins,
          background: "#1e3a6e",
          color: "#fff",
          borderRadius: "14px",
          py: 1.8,
          mt: 1,
          mb: 1.5,
          fontSize: "1rem",
          fontWeight: 700,
          textTransform: "none",
          boxShadow: "0 6px 24px rgba(15,31,69,0.4)",
          "&:hover": { background: "#162d57" },
        }}
      >
        Pesan
      </Button>

      <Button
        variant="outlined"
        fullWidth
        onClick={() => navigate("/profile")}
        sx={{
          ...poppins,
          background: "#fff",
          color: "#1e3a6e",
          border: "2px solid #1e3a6e",
          borderRadius: "14px",
          py: 1.75,
          fontSize: "1rem",
          fontWeight: 600,
          textTransform: "none",
          "&:hover": { background: "#f0f4ff", border: "2px solid #1e3a6e" },
        }}
      >
        Keluar Halaman
      </Button>

      <Loading open={loading} />

      <ResponseAlert
        response={response}
        success={success}
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
      />
    </Box>
  );
}
